/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Tenant } from '../types';
import api from '../services/api';

interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  /** true on the main domain (global marketplace, no tenant). */
  isMainDomain: boolean;
  refreshTenant: () => Promise<void>;
}

const DEFAULTS = { primary: '#FFD400', secondary: '#FFC107' };

const TenantContext = createContext<TenantContextType | null>(null);

function detectMainDomain(): boolean {
  const hostname = window.location.hostname;
  const exactLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const localhostSubdomain = hostname.endsWith('.localhost') && !exactLocalhost;
  const parts = hostname.split('.');
  return (
    exactLocalhost ||
    (!localhostSubdomain && parts.length <= 2) || // apex like myezwheels.com
    hostname.startsWith('www.') ||
    hostname.startsWith('app.') ||
    hostname.startsWith('admin.')
  );
}

function applyBranding(tenant: Tenant | null) {
  const root = document.documentElement.style;
  root.setProperty('--color-primary', tenant?.primaryColor || DEFAULTS.primary);
  root.setProperty('--color-secondary', tenant?.secondaryColor || DEFAULTS.secondary);
  if (tenant?.accentColor) root.setProperty('--color-accent', tenant.accentColor);
  else root.setProperty('--color-accent', tenant?.primaryColor || DEFAULTS.primary);

  // Custom CSS from the Design editor, injected once into a dedicated <style>.
  let styleEl = document.getElementById('tenant-custom-css') as HTMLStyleElement | null;
  if (tenant?.customCss) {
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'tenant-custom-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = tenant.customCss;
  } else if (styleEl) {
    styleEl.textContent = '';
  }

  // Background image from the Design editor → fixed, full-viewport site backdrop. Sits over the
  // body's --color-bg color; content sections (opaque hero/cards) float on top, and it shows on
  // transparent pages (About / Terms / Contact) and behind the hero when there's no banner.
  if (tenant?.backgroundImageUrl) {
    document.body.style.backgroundImage = `url("${tenant.backgroundImageUrl}")`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundRepeat = 'no-repeat';
  } else {
    document.body.style.backgroundImage = '';
  }

  // Favicon from the Design editor.
  if (tenant?.faviconUrl) {
    let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = tenant.faviconUrl;
  }
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMainDomain, setIsMainDomain] = useState(true);

  const refreshTenant = async () => {
    setIsLoading(true);
    const main = detectMainDomain();
    setIsMainDomain(main);

    if (main) {
      setTenant(null);
      applyBranding(null);
      setIsLoading(false);
      return;
    }

    try {
      const data = await api.getTenant();
      setTenant(data);
      applyBranding(data);
      if (data?.displayName) document.title = `${data.displayName} — My E-Z Wheels`;
    } catch (err: any) {
      // 404 = no tenant for this host; fall back to global behavior.
      setTenant(null);
      setIsMainDomain(true);
      applyBranding(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, isLoading, isMainDomain, refreshTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider');
  return ctx;
}
